import type { CatalogEntry } from "@cfg/api-contract";
import { useLocale } from "@cfg/configurator-ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useApp } from "./app-context.tsx";

/** The catalog as cards: families grouped (see `groupFamilies`), variant names in the active locale. */
export function useCatalogGroups(entries: CatalogEntry[] | undefined): CatalogGroup[] | undefined {
  const { designs } = useLocale();
  return useMemo(
    () => entries && groupFamilies(entries, (d) => (d.variant ? (designs ? designs.text(d.id, d.variant) : d.variant) : d.title)),
    [entries, designs],
  );
}

/** The brand's catalog (latest version of each design), titles and descriptions in the active locale. */
export function useCatalog() {
  const { brand, registry } = useApp();
  const { designs } = useLocale();
  const select = useCallback(
    (entries: CatalogEntry[]): CatalogEntry[] =>
      designs
        ? entries.map((d) => ({ ...d, title: designs.text(d.id, d.title), ...(d.description ? { description: designs.text(d.id, d.description) } : {}) }))
        : entries,
    [designs],
  );
  return useQuery({ queryKey: ["catalog", brand.config.id], queryFn: () => registry.catalog(), select });
}

/** A catalog card: one design, or a family (wall vs SKÅDIS mount) under its first member. */
export interface CatalogGroup {
  design: CatalogEntry;
  /** Every design the card stands for, the shown one first. */
  members: CatalogEntry[];
  /** Variant names, in catalog order; empty for a design outside a family. */
  variants: string[];
}

/**
 * One card per family (same params, other mount), placed where its first
 * member is and opening that one; designs outside families stay as they are.
 */
export function groupFamilies(entries: CatalogEntry[], variantText: (d: CatalogEntry) => string = (d) => d.variant ?? d.title): CatalogGroup[] {
  const out: CatalogGroup[] = [];
  const byFamily = new Map<string, CatalogGroup>();
  for (const d of entries) {
    const g = d.family ? byFamily.get(d.family) : undefined;
    if (g) {
      g.members.push(d);
      g.variants.push(variantText(d));
      continue;
    }
    const group = { design: d, members: [d], variants: d.family ? [variantText(d)] : [] };
    if (d.family) byFamily.set(d.family, group);
    out.push(group);
  }
  // A "family" of one (the other member hidden for this brand) is just a design.
  for (const g of out) if (g.members.length < 2) g.variants = [];
  return out;
}
